import React, { Fragment, FC, useState, ChangeEvent, useEffect } from 'react';
import {
    Typography,
    Box,
    Switch,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { MaterialBatch, LabMaterial } from 'models/material';
import { useTranslation } from 'react-i18next';
import MaterialBatchItem from './material-batch-item.component';
import MaterialBatchItemInput from './material-batch-item-input.component';
import { CONSTANTS } from 'config/constants';

type MaterialBatchManagerProps = {
    materialToEdit?: LabMaterial;
    onBatches: (batches: MaterialBatch[]) => void;
    onUseInventory?: (useInventory: boolean) => void;
    openBatchManagerInDialog?: boolean;
    onClose?: () => void;
    onSaveBatchChanges?: (batches: MaterialBatch[]) => void;
};
const MaterialBatchManager: FC<MaterialBatchManagerProps> = ({
    materialToEdit,
    onBatches,
    onUseInventory,
    openBatchManagerInDialog,
    onClose,
    onSaveBatchChanges,
}: MaterialBatchManagerProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [useInventory, setUseInventory] = useState<boolean | undefined>(materialToEdit?.batchInventoryManagement);
    const [batches, setBatches] = useState<MaterialBatch[]>([]);
    const [closedBatches, setClosedBatches] = useState<MaterialBatch[]>([]);
    const [showClosedBatches, setShowClosedBatches] = useState(false);
    const [disableSaveBatches, setDisableSaveBatches] = useState(true);

    useEffect(() => {
        setUseInventory(materialToEdit?.batchInventoryManagement || false);
        const batches = materialToEdit?.batches?.filter((batch) => !batch.closed);
        const closedBatches = materialToEdit?.batches?.filter((batch) => batch.closed);
        if (batches) {
            const batchesArr = [];
            for (const batch of batches) {
                batchesArr.push({ ...batch, isSaved: true });
            }
            setBatches(batchesArr || []);
        }
        if (closedBatches) {
            const batchesArr = [];
            for (const batch of closedBatches) {
                batchesArr.push({ ...batch, isSaved: true });
            }
            setClosedBatches(batchesArr || []);
        }
    }, [materialToEdit]);

    const handleMoveUpBatch = (batch: MaterialBatch, index: number) => {
        setDisableSaveBatches(false);
        const batchesArr = [...batches];
        batchesArr.splice(index, 1);
        batchesArr.splice(index - 1, 0, { ...batch });
        setBatches(batchesArr);
        onBatches(batchesArr.concat(closedBatches));
    };
    const handleMoveDownBatch = (batch: MaterialBatch, index: number) => {
        setDisableSaveBatches(false);
        const batchesArr = [...batches];
        batchesArr.splice(index, 1);
        batchesArr.splice(index + 1, 0, { ...batch });
        setBatches(batchesArr);
        onBatches(batchesArr.concat(closedBatches));
    };
    const handleCloseBatch = (batch: MaterialBatch, index: number) => {
        setDisableSaveBatches(false);
        // add batch to closed batches array
        const closedBatchesArr = [...closedBatches];
        closedBatchesArr.push({ ...batch, closed: true, closedDate: new Date() });
        setClosedBatches(closedBatchesArr);
        // remove batch from batches array
        const batchesArr = [...batches];
        batchesArr.splice(index, 1);
        setBatches(batchesArr);
        onBatches(batchesArr.concat(closedBatchesArr));
    };

    const handleQuantityChange = (quantity: number, batch: MaterialBatch, index: number) => {
        const batchesArr = [...batches];
        batchesArr.splice(index, 1, { ...batch, quantity });
        setBatches(batchesArr);
        onBatches(batchesArr);
    };

    const handleUseInventory = (event: ChangeEvent<HTMLInputElement>) => {
        const useInventory = event.target.checked;
        setUseInventory(useInventory);
        onUseInventory?.(useInventory);
    };

    const saveBatch = (batch: MaterialBatch, index: number) => {
        setDisableSaveBatches(false);
        setBatches([...batches.slice(0, index), { ...batch, isSaved: true }, ...batches.slice(index + 1)]);
        onBatches(
            [...batches.slice(0, index), { ...batch, isSaved: true }, ...batches.slice(index + 1)].concat(
                closedBatches,
            ),
        );
    };

    const toggleShowClosedBatches = () => {
        setShowClosedBatches((prev) => !prev);
    };
    const handleAddBatch = () => {
        setBatches([...batches, { isSaved: false } as MaterialBatch]);
    };
    const isAddingBatch = (): boolean => {
        return batches.some((batch) => !batch.isSaved);
    };

    return (
        <Box>
            <Box mb={1} display="flex" alignItems="center">
                {!openBatchManagerInDialog ? (
                    <Fragment>
                        <Typography variant="h3">{translate('lab-settings:BATCH_INVENTORY')}</Typography>
                        <Switch checked={useInventory} onChange={handleUseInventory} />
                    </Fragment>
                ) : (
                    <Typography variant="h3">{materialToEdit?.description}</Typography>
                )}
                {useInventory ? (
                    <Box flexGrow={1} style={{ textAlign: 'right' }}>
                        <Button size="small" onClick={toggleShowClosedBatches}>
                            {!showClosedBatches
                                ? translate('lab-settings:SHOW_CLOSED_BATCHES')
                                : translate('lab-settings:SHOW_OPEN_BATCHES')}
                        </Button>
                    </Box>
                ) : undefined}
            </Box>
            <Box>
                {useInventory ? (
                    <Fragment>
                        {(batches.length && !showClosedBatches) || (closedBatches.length && showClosedBatches) ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('actions-materials:BATCH_NO')}</TableCell>
                                            <TableCell>{translate('actions-materials:QUANTITY_FULL_BATCH')}</TableCell>
                                            <TableCell>{translate('common:CREATED')}</TableCell>
                                            {!showClosedBatches ? (
                                                <TableCell></TableCell>
                                            ) : (
                                                <Fragment>
                                                    <TableCell>{translate('lab-settings:CLOSED')}</TableCell>
                                                    <TableCell></TableCell>
                                                </Fragment>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(!showClosedBatches ? batches : closedBatches).map(
                                            (batch: MaterialBatch, index: number) => (
                                                <TableRow key={index}>
                                                    {batch.isSaved ? (
                                                        <MaterialBatchItem
                                                            index={index}
                                                            batch={batch}
                                                            onQuantityChange={handleQuantityChange}
                                                            onMoveUpBatch={handleMoveUpBatch}
                                                            onMoveDownBatch={handleMoveDownBatch}
                                                            onCloseBatch={handleCloseBatch}
                                                        />
                                                    ) : (
                                                        <MaterialBatchItemInput index={index} saveBatch={saveBatch} />
                                                    )}
                                                </TableRow>
                                            ),
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Fragment>
                                {!batches.length && !showClosedBatches && (
                                    <Alert severity="info">{translate('lab-settings:NO_OPEN_BATCHES_FOUND')}</Alert>
                                )}
                                {!closedBatches.length && showClosedBatches && (
                                    <Alert severity="info">{translate('lab-settings:NO_CLOSED_BATCHES_FOUND')}</Alert>
                                )}
                            </Fragment>
                        )}
                        {!showClosedBatches && !isAddingBatch() ? (
                            <Box display="flex" justifyContent="space-between" mt={2}>
                                <Button startIcon={<AddIcon />} onClick={handleAddBatch}>
                                    {translate('lab-settings:ADD_BATCH')}
                                </Button>
                                {openBatchManagerInDialog && (
                                    <Box>
                                        <Button
                                            variant="contained"
                                            disabled={disableSaveBatches}
                                            onClick={() => onSaveBatchChanges?.(batches.concat(closedBatches))}
                                        >
                                            {translate('actions-materials:SAVE_CHANGES')}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box display="flex" justifyContent="right" mt={2}>
                                {openBatchManagerInDialog && (
                                    <Button
                                        variant="contained"
                                        disabled={disableSaveBatches}
                                        onClick={() => onSaveBatchChanges?.(batches.concat(closedBatches))}
                                    >
                                        {translate('actions-materials:SAVE_CHANGES')}
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Fragment>
                ) : (
                    <Alert severity="info">{translate('actions-materials:BATCH_INVENTORY_INACTIVE')}</Alert>
                )}
            </Box>
        </Box>
    );
};

export default MaterialBatchManager;
